/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Text from "../../ui/Text";
import Flex from "../../ui/Flex";
import FormatPrice from "../../formatters/FormatPrice";
import { useInvoiceCalculations } from "../../../hooks/checkout/useInvoiceCalculations";

const StyledSummaryTotals = styled(Flex)`
  padding-right: 1rem;
`;

const SummaryTotalsItem = styled(Flex)`
  text-align: right;
`;

const SummaryTotals = ({ invoice }) => {
  const {
    hasDiscount,
    hasTip,
    hasTaxes,
    hasRestAmountWithDeposit,
    isDepositPayment,
    deposit,
    discount,
    tip,
    totalToPay,
    payableAmount,
    restAmount,
    taxes,
    currency,
  } = useInvoiceCalculations(invoice);

  return (
    <StyledSummaryTotals $direction="column" $align="end" $gap="0.5rem">
      {hasDiscount && (
        <SummaryTotalsItem $align="center" $gap="0.5rem">
          <Text weight="bold" lineClamp={1}>
            invoice_line_discount
          </Text>
          <Text weight="bold">
            <FormatPrice value={discount} currency={currency} />
          </Text>
        </SummaryTotalsItem>
      )}

      {hasTip && (
        <SummaryTotalsItem $align="center" $gap="0.5rem">
          <Text weight="bold" lineClamp={1}>
            Tip
          </Text>
          <Text weight="bold">
            <FormatPrice value={tip.amount} currency={currency} />
          </Text>
        </SummaryTotalsItem>
      )}

      {hasTaxes &&
        taxes.map(
          (tax) =>
            tax.amount > 0 && (
              <SummaryTotalsItem key={tax.id} $align="center" $gap="0.5rem">
                <Text weight="bold" lineClamp={1}>
                  {tax.name}
                </Text>
                <Text weight="bold">
                  <FormatPrice value={tax.amount} currency={currency} />
                </Text>
              </SummaryTotalsItem>
            )
        )}

      {isDepositPayment && !hasDiscount && (
        <SummaryTotalsItem $align="center" $gap="0.5rem">
          <Text weight="bold" lineClamp={1}>
            Total deposit
          </Text>
          <Text weight="bold">
            <FormatPrice value={deposit} currency={currency} />
          </Text>
        </SummaryTotalsItem>
      )}

      {hasRestAmountWithDeposit && !hasDiscount && (
        <>
          <SummaryTotalsItem $align="center" $gap="0.5rem">
            <Text weight="bold" lineClamp={1}>
              invoice_total_rest_amount
            </Text>
            <Text weight="bold">
              <FormatPrice value={restAmount} currency={currency} />
            </Text>
          </SummaryTotalsItem>
          <SummaryTotalsItem $align="center" $gap="0.5rem">
            <Text weight="bold" lineClamp={1}>
              invoice_total_amount_with_rest_amount
            </Text>
            <Text weight="bold">
              <FormatPrice value={totalToPay} currency={currency} />
            </Text>
          </SummaryTotalsItem>
        </>
      )}

      <SummaryTotalsItem $align="center" $gap="0.5rem">
        <Text color="dark" weight="bold" lineClamp={1}>
          {hasRestAmountWithDeposit ? "invoice_payable_now" : "invoice_total_amount"}
        </Text>
        <Text color="dark" weight="bold">
          <FormatPrice value={payableAmount} currency={currency} />
        </Text>
      </SummaryTotalsItem>
    </StyledSummaryTotals>
  );
};

export default SummaryTotals;
